import { ChangeEvent, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';

import { requestCreateFormTemplate } from '@atom/actions/formTemplateActions';
import { retrieveInventorySchemas } from '@atom/actions/inventorySchemaActions';
import {
  Icon,
  IconButton,
  Modal,
  Progress,
  Select,
  TextField,
} from '@atom/mui';
import { AddIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { InventorySchemaItem } from '@atom/types/schema';
import { RootState } from '@atom/types/store';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import {
  CreateFormModalState,
  formModalStateReducer,
  initialState,
} from './CreateFormModalState';

import classes from './formRepository.module.css';

const { MenuItem } = Select;

const styles = {
  floatingLabelStyle: {
    fontSize: fonts.md,
    color: colors.neutral.gray,
  },
  textFieldStyle: {
    color: colors.neutral.dark,
    fontSize: fonts.md,
  },
  underlineStyle: {
    borderColor: colors.neutral.silver,
  },
  selectFieldStyle: {
    input: {
      fontSize: fonts.md,
      color: colors.neutral.black,
      whiteSpace: 'normal',
      padding: '0.25em 0',
    },
    selected: {
      fontSize: fonts.md,
      color: colors.brand.blue,
      whiteSpace: 'normal',
      padding: '0.25em 0',
    },
    iconStyle: {
      fill: colors.neutral.dim,
    },
  },
};

const CreateFormModal = () => {
  const dispatch = useDispatch();
  const [formModelState, dispatchState] = useReducer(
    formModalStateReducer,
    initialState,
  );
  const { open, rootSchema, name, errors }: CreateFormModalState =
    formModelState;

  const inventorySchemas = useSelector(
    (state: RootState) => state.inventorySchemas,
  );
  const loading = useSelector(
    (state: RootState) => state.loading.loadingFormTemplateCreation,
  );

  useEffect(() => {
    if (R.isNil(inventorySchemas) || R.isEmpty(inventorySchemas)) {
      dispatch(retrieveInventorySchemas({ rootSchemas: true }));
    }
  }, []);

  const handleCloseModal = () => {
    dispatchState({
      type: 'UPDATE_STATE',
      data: initialState,
    });
  };

  const handleOpenModal = () => {
    dispatchState({
      type: 'UPDATE_STATE',
      data: { open: true },
    });
  };

  const handleConfirm = () => {
    if (!name) {
      dispatchState({
        type: 'UPDATE_STATE',
        data: {
          errors: {
            ...errors,
            name: 'The form title must be provided',
          },
        },
      });
      return;
    }

    dispatchState({
      type: 'UPDATE_STATE',
      data: {
        errors: {
          ...errors,
          name: '',
        },
      },
    });
    dispatch(
      requestCreateFormTemplate({
        name,
        ...(!R.isNil(rootSchema) ? { schemaId: rootSchema.id } : {}),
      }),
    );
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatchState({
      type: 'UPDATE_STATE',
      data: { name: value },
    });
  };

  const onDataTypeChange = (event: any) => {
    const { value } = event.target;
    dispatchState({
      type: 'UPDATE_STATE',
      data: { rootSchema: value },
    });
  };

  const isCreationDisabled = (): boolean => {
    return R.isEmpty(name);
  };

  const getSchemaTextAndColor = (schema: InventorySchemaItem) => {
    const icon = schemaUtilities.getSchemaIconFromSchemaOrAsset(schema);

    return (
      <div className={classes.schemaMenuItemContainer}>
        {icon}
        <div className={classes.schemaMenuItemTitle}>{schema.name}</div>
      </div>
    );
  };

  const creationDisabled = isCreationDisabled();

  const dialogContent = loading ? (
    <div className={classes.modalSpinnerContainer}>
      <Progress />
    </div>
  ) : (
    <div className={classes.modalContainer}>
      <div>
        <TextField
          label="Form Title"
          value={name}
          name="name"
          onChange={onChange}
        />
      </div>
      <div>
        <Select
          fullWidth
          label="Inventory Type (Optional)"
          style={styles.textFieldStyle}
          onChange={onDataTypeChange}
          value={rootSchema}
        >
          {inventorySchemas.map((schema: InventorySchemaItem) => {
            const primaryText = getSchemaTextAndColor(schema);
            return (
              <MenuItem key={schema.id} value={schema}>
                {primaryText}
              </MenuItem>
            );
          })}
        </Select>
      </div>
    </div>
  );

  return (
    <div className={classes.iconContainer}>
      <IconButton tooltip="Create" onClick={handleOpenModal}>
        <Icon color={colors.neutral.white} icon={AddIcon} />
      </IconButton>
      <Modal
        title="Create Form"
        onConfirm={handleConfirm}
        confirmButtonText="Create"
        onCancel={handleCloseModal}
        disabled={creationDisabled}
        open={open}
      >
        {dialogContent}
      </Modal>
    </div>
  );
};

export default CreateFormModal;
